import { AlertTriangle, CheckCircle, Clock, HelpCircle, Wrench, XCircle } from 'lucide-react';
import type { CapabilityProbe, ProbeStatus } from '../api/doctor';

/**
 * 能力探测区块（B7）：真实调用 Agent 外部能力后的逐项状态 + 修复处方。
 *
 * - 接口无 probes 字段（旧后端）或列表为空时返回 null，区块不渲染；
 * - 每项展示状态图标（ok 绿 / broken 红 / timeout 黄 / error 红 / skipped 灰）
 *   + 人话详情 + 修复处方，风格对齐体检 issue 列表。
 */

const STATUS_META: Record<ProbeStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  ok: { icon: CheckCircle, color: 'var(--color-success)', label: '正常' },
  broken: { icon: XCircle, color: 'var(--color-error)', label: '不可用' },
  timeout: { icon: Clock, color: 'var(--color-warning)', label: '超时' },
  error: { icon: AlertTriangle, color: 'var(--color-error)', label: '异常' },
  skipped: { icon: HelpCircle, color: 'var(--color-muted, #8B949E)', label: '未配置' },
};

export default function CapabilityProbes({ probes }: { probes?: CapabilityProbe[] }) {
  if (!probes || probes.length === 0) return null;

  return (
    <div className="mt-4" data-testid="capability-probes">
      <div className="flex items-center gap-2 mb-2">
        <strong style={{ fontSize: 14 }}>能力探测</strong>
        <span className="text-muted" style={{ fontSize: 12 }}>
          已真实调用外部能力验证可用性
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {probes.map((probe) => {
          const meta = STATUS_META[probe.status] || STATUS_META.error;
          const Icon = meta.icon;
          return (
            <div
              key={probe.name}
              data-testid={`probe-${probe.name}`}
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: 10,
                alignItems: 'flex-start',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid var(--border-default)',
                background: 'transparent',
              }}
            >
              <Icon size={16} style={{ color: meta.color, flexShrink: 0, marginTop: 2 }} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 600, fontSize: 13 }}>
                  {probe.label}
                  <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                    {meta.label}
                  </span>
                </span>
                <span className="text-muted" style={{ display: 'block', fontSize: 13, marginTop: 2 }}>
                  {probe.detail}
                </span>
                {probe.prescription && (
                  <span style={{ display: 'block', fontSize: 13, marginTop: 4, color: 'var(--color-accent, #58A6FF)' }}>
                    <Wrench size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    修复：{probe.prescription}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
